<template>
  <div>
    <PageHeader title="Quick Send" />
    <b-card no-body>
      <b-tabs card>
        <!-- Single SMS Tab -->
        <b-tab title="Single SMS" active>
          <b-form @submit.prevent="handleSend">
            <b-form-group label="Select Contact">
              <v-select v-model="form.contact_id" :options="contacts" label="text" :reduce="c => c.value" placeholder="Search contacts..." />
            </b-form-group>
            <b-form-group label="Use Template (optional)">
              <b-form-select v-model="form.template_id" :options="templateOptions" />
            </b-form-group>
            <b-form-group label="Or Type Message">
              <b-form-textarea v-model="form.body" rows="4" />
              <small class="text-muted">{{ (form.body || '').length }} characters</small>
            </b-form-group>
            <b-button type="submit" variant="primary" :disabled="isSending">{{ isSending ? 'Sending...' : 'Send SMS' }}</b-button>
          </b-form>
        </b-tab>

        <!-- Bulk SMS Tab -->
        <b-tab title="Bulk SMS">
          <b-form @submit.prevent="handleBulkSend">
            <b-form-group label="Recipient Mode">
              <b-form-radio-group v-model="bulkMode" :options="bulkModeOptions" buttons button-variant="outline-primary" size="sm" />
            </b-form-group>

            <b-form-group v-if="bulkMode === 'contacts'" label="Select Contacts">
              <v-select v-model="bulkForm.contact_ids" :options="contacts" label="text" :reduce="c => c.value" multiple placeholder="Search and select contacts..." />
            </b-form-group>

            <b-form-group v-if="bulkMode === 'tags'" label="Select Tags">
              <v-select v-model="bulkForm.tag_ids" :options="tags" label="name" :reduce="t => t.id" multiple placeholder="Search and select tags..." />
            </b-form-group>

            <b-form-group label="Use Template (optional)">
              <b-form-select v-model="bulkForm.template_id" :options="templateOptions" />
            </b-form-group>
            <b-form-group label="Or Type Message">
              <b-form-textarea v-model="bulkForm.body" rows="4" />
              <small class="text-muted">{{ (bulkForm.body || '').length }} characters</small>
            </b-form-group>
            <b-button type="submit" variant="primary" :disabled="isBulkSending">{{ isBulkSending ? 'Sending...' : 'Send Bulk SMS' }}</b-button>
          </b-form>
        </b-tab>
      </b-tabs>
    </b-card>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import PageHeader from '../../components/Ui/PageHeader.vue';

export default {
  name: 'SendSMS',
  components: { PageHeader },
  data() {
    return {
      // Single send
      form: { contact_id: null, template_id: null, body: '' },
      isSending: false,
      // Bulk send
      bulkMode: 'contacts',
      bulkModeOptions: [
        { text: 'Select Contacts', value: 'contacts' },
        { text: 'Select by Tag', value: 'tags' },
      ],
      bulkForm: { contact_ids: [], tag_ids: [], template_id: null, body: '' },
      isBulkSending: false,
      // Shared data
      contacts: [],
      templates: [],
      tags: [],
    };
  },
  computed: {
    templateOptions() {
      return [{ value: null, text: 'None' }, ...this.templates.map(t => ({ value: t.id, text: t.name }))];
    },
  },
  methods: {
    ...mapActions('sms', ['sendSMS', 'sendBulkSMS']),
    ...mapActions('contact', ['getAllContacts']),
    ...mapActions('smsTemplate', ['getAllSMSTemplates']),
    ...mapActions('tag', ['getAllTags']),
    async handleSend() {
      if (!this.form.contact_id) { this.makeToast('danger', 'Select a contact'); return; }
      this.isSending = true;
      try {
        await this.sendSMS(this.form);
        this.makeToast('success', 'SMS sent');
        this.form = { contact_id: null, template_id: null, body: '' };
      } catch (err) { this.makeToast('danger', 'Send failed'); }
      finally { this.isSending = false; }
    },
    async handleBulkSend() {
      const hasContacts = this.bulkMode === 'contacts' && this.bulkForm.contact_ids.length > 0;
      const hasTags = this.bulkMode === 'tags' && this.bulkForm.tag_ids.length > 0;
      if (!hasContacts && !hasTags) {
        this.makeToast('danger', this.bulkMode === 'contacts' ? 'Select at least one contact' : 'Select at least one tag');
        return;
      }
      this.isBulkSending = true;
      try {
        const payload = {
          contact_ids: this.bulkMode === 'contacts' ? this.bulkForm.contact_ids : [],
          tag_ids: this.bulkMode === 'tags' ? this.bulkForm.tag_ids : [],
          template_id: this.bulkForm.template_id,
          body: this.bulkForm.body,
        };
        await this.sendBulkSMS(payload);
        this.makeToast('success', 'Bulk SMS sent');
        this.bulkForm = { contact_ids: [], tag_ids: [], template_id: null, body: '' };
      } catch (err) { this.makeToast('danger', 'Bulk send failed'); }
      finally { this.isBulkSending = false; }
    },
  },
  async created() {
    try {
      const [cRes, tRes, tagRes] = await Promise.all([
        this.getAllContacts({ limit: 1000 }),
        this.getAllSMSTemplates(),
        this.getAllTags(),
      ]);
      const contactData = cRes.data.results || cRes.data || [];
      this.contacts = contactData.map(c => ({ value: c.id, text: `${c.first_name} ${c.last_name} (${c.phone || 'no phone'})` }));
      this.templates = tRes.data.results || tRes.data || [];
      this.tags = tagRes.data.results || tagRes.data || [];
    } catch (err) { /* ignore */ }
  },
};
</script>
